import { useCallback, useEffect, useState } from "react";
import { CreateCampaignModal } from "../../components/WhatsAppAutoMessageSender/AddCampaignModal";
import { RootDispatch, RootState } from "../../store";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCampaigns,
  pauseCampaign,
  resumeCampaign,
} from "../../store/slices/whatsappCampaignSlice";
import WhatsappCampaign from "../../models/whatsappCampaigns/campaign.model";
import { Edit, Pause, Play, Loader2, FileText } from "lucide-react";
import { formatDate } from "../../utils/formatDateUtils";
import { useNavigate } from "react-router-dom";

const CampaignsPage = () => {
  const dispatch: RootDispatch = useDispatch();
  const navigate = useNavigate();
  const campaigns = useSelector(
    (state: RootState) => state.whatsappCampaign.campaigns,
  );
  const loadingFetchCampaigns = useSelector(
    (state: RootState) => state.whatsappCampaign.loading.fetchCampaigns,
  );
  const loadingPause = useSelector(
    (state: RootState) => state.whatsappCampaign.loading.pauseCampaign,
  );
  const loadingResume = useSelector(
    (state: RootState) => state.whatsappCampaign.loading.resumeCampaign,
  );

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [selectedCampaign, setSelectedCampaign] = useState<any>(null);

  const fetchCampaignsData = useCallback(async () => {
    await dispatch(fetchCampaigns({ page, limit }));
  }, [dispatch, page, limit]);

  useEffect(() => {
    fetchCampaignsData();
  }, [fetchCampaignsData]);

  const [campaignModalOpen, setCampaignModalOpen] = useState(false);

  const handleAddCampaign = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(true);
  };

  const handleEdit = (c: any) => {
    setSelectedCampaign(c);
    setCampaignModalOpen(true);
  };

  const handleViewLogs = (campaignId: string) => {
    navigate(`/campaigns/${campaignId}`);
  };

  const handlePause = async (campaignId: string) => {
    await dispatch(pauseCampaign({ campaignId }));
    fetchCampaignsData();
  };

  const handleResume = async (campaignId: string) => {
    await dispatch(resumeCampaign({ campaignId }));
    fetchCampaignsData();
  };

  const handlePrev = () => {
    if (page > 1) {
      const np = page - 1;
      setPage(np);
      dispatch(fetchCampaigns({ page: np, limit }));
    }
  };

  const handleNext = () => {
    const np = page + 1;
    setPage(np);
    dispatch(fetchCampaigns({ page: np, limit }));
  };

  const getStatusClasses = (status?: string) => {
    const normalizedStatus = (status || "").toLowerCase();

    if (
      normalizedStatus.includes("active") ||
      normalizedStatus.includes("sent") ||
      normalizedStatus.includes("success")
    ) {
      return "bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200";
    }

    if (
      normalizedStatus.includes("draft") ||
      normalizedStatus.includes("pending") ||
      normalizedStatus.includes("scheduled")
    ) {
      return "bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200";
    }

    if (
      normalizedStatus.includes("failed") ||
      normalizedStatus.includes("cancel") ||
      normalizedStatus.includes("inactive")
    ) {
      return "bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200";
    }

    return "bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-200";
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-slate-100 p-4 box-border md:p-6">
      <div className="mb-5 flex w-full items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-800 md:text-2xl">
            Campaigns
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Manage your messaging campaigns
          </p>
        </div>

        <button
          type="button"
          className="cursor-pointer rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-[0_2px_8px_rgba(37,99,235,0.2)] transition hover:bg-blue-700"
          onClick={handleAddCampaign}
        >
          Add Campaign
        </button>
      </div>

      <div className="flex-1 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.08)]">
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-3 md:px-6">
          <div className="flex items-center gap-2">
            <div className="text-sm font-medium text-slate-600">Overview</div>
            {loadingFetchCampaigns && (
              <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
            )}
          </div>
          <div className="text-sm text-slate-500">Page {page}</div>
        </div>

        <div className="hidden overflow-x-auto md:block">
          {loadingFetchCampaigns ? (
            <div className="p-6">
              <div className="animate-pulse">
                <div className="h-6 w-1/4 rounded bg-slate-200 mb-4" />
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between border-t border-slate-200 py-4"
                  >
                    <div className="h-4 w-1/4 rounded bg-slate-200" />
                    <div className="h-4 w-1/6 rounded bg-slate-200" />
                    <div className="h-4 w-1/6 rounded bg-slate-200" />
                    <div className="h-4 w-1/6 rounded bg-slate-200" />
                    <div className="h-4 w-1/6 rounded bg-slate-200" />
                    <div className="h-6 w-24 rounded bg-slate-200" />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <table className="min-w-full border-separate border-spacing-0">
              <thead>
                <tr className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
                  <th className="px-6 py-3">Campaign</th>
                  <th className="px-6 py-3">Template</th>
                  <th className="px-6 py-3">Sender</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">Created</th>
                  <th className="px-6 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {campaigns && campaigns.length > 0 ? (
                  campaigns.map((c: WhatsappCampaign) => (
                    <tr
                      key={c._id}
                      className="border-t border-slate-200 transition-colors hover:bg-slate-50"
                    >
                      <td className="px-6 py-4 align-middle">
                        <div className="font-semibold text-slate-800">
                          {c.name || "Untitled"}
                        </div>
                      </td>
                      <td className="px-6 py-4 align-middle text-sm text-slate-600">
                        {c.whatsapp.templateName || "—"}
                      </td>
                      <td className="px-6 py-4 align-middle text-sm text-slate-600">
                        {c.whatsapp.phoneNumber || "—"}
                      </td>
                      <td className="px-6 py-4 align-middle">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${getStatusClasses(
                            c.status,
                          )}`}
                        >
                          {c.status || "Unknown"}
                        </span>
                      </td>
                      <td className="px-6 py-4 align-middle text-sm text-slate-600">
                        {formatDate(c.createdAt)}
                      </td>
                      <td className="px-6 py-4 text-right align-middle">
                        <div className="flex items-center justify-end gap-2">
                          <div className="group relative inline-block">
                            <button
                              type="button"
                              onClick={() => handleEdit(c)}
                              aria-label="Edit"
                              title="Edit"
                              className="inline-flex items-center rounded-md border border-blue-200 bg-white px-3 py-1.5 text-sm font-medium text-blue-700 transition hover:bg-blue-50 disabled:opacity-50"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <span className="pointer-events-none absolute bottom-full left-1/2 mb-2 hidden -translate-x-1/2 whitespace-nowrap rounded bg-slate-800 px-2 py-1 text-xs text-white group-hover:block">
                              Edit
                            </span>
                          </div>

                          <div className="group relative inline-block">
                            <button
                              type="button"
                              onClick={() => handleViewLogs(c._id)}
                              aria-label="View logs"
                              title="View logs"
                              className="inline-flex items-center rounded-md border border-violet-200 bg-white px-3 py-1.5 text-sm font-medium text-violet-700 transition hover:bg-violet-50 disabled:opacity-50"
                            >
                              <FileText className="h-4 w-4" />
                            </button>
                            <span className="pointer-events-none absolute bottom-full left-1/2 mb-2 hidden -translate-x-1/2 whitespace-nowrap rounded bg-slate-800 px-2 py-1 text-xs text-white group-hover:block">
                              View logs
                            </span>
                          </div>
                          {(() => {
                            const normalized = (c.status || "").toLowerCase();
                            const isPaused =
                              normalized.includes("paused") ||
                              normalized.includes("pause");
                            const isRunningOrQueued =
                              normalized.includes("active") ||
                              normalized.includes("running") ||
                              normalized.includes("queued") ||
                              normalized.includes("sending") ||
                              normalized.includes("pending") ||
                              normalized.includes("scheduled");

                            if (isPaused) {
                              return (
                                <div className="group relative inline-block">
                                  <button
                                    type="button"
                                    onClick={() => handleResume(c._id)}
                                    aria-label="Resume"
                                    title={
                                      loadingResume ? "Resuming..." : "Resume"
                                    }
                                    disabled={loadingResume}
                                    className="inline-flex items-center rounded-md border border-emerald-200 bg-white px-3 py-1.5 text-sm font-medium text-emerald-700 transition hover:bg-emerald-50 disabled:opacity-50"
                                  >
                                    <Play className="h-4 w-4" />
                                  </button>
                                  <span className="pointer-events-none absolute bottom-full left-1/2 mb-2 hidden -translate-x-1/2 whitespace-nowrap rounded bg-slate-800 px-2 py-1 text-xs text-white group-hover:block">
                                    {loadingResume ? "Resuming..." : "Resume"}
                                  </span>
                                </div>
                              );
                            }

                            if (isRunningOrQueued) {
                              return (
                                <div className="group relative inline-block">
                                  <button
                                    type="button"
                                    onClick={() => handlePause(c._id)}
                                    aria-label="Pause"
                                    title={
                                      loadingPause ? "Pausing..." : "Pause"
                                    }
                                    disabled={loadingPause}
                                    className="inline-flex items-center rounded-md border border-amber-200 bg-white px-3 py-1.5 text-sm font-medium text-amber-700 transition hover:bg-amber-50 disabled:opacity-50"
                                  >
                                    <Pause className="h-4 w-4" />
                                  </button>
                                  <span className="pointer-events-none absolute bottom-full left-1/2 mb-2 hidden -translate-x-1/2 whitespace-nowrap rounded bg-slate-800 px-2 py-1 text-xs text-white group-hover:block">
                                    {loadingPause ? "Pausing..." : "Pause"}
                                  </span>
                                </div>
                              );
                            }

                            return null;
                          })()}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-6 py-12 text-center text-sm text-slate-400"
                    >
                      No campaigns found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        <div className="space-y-3 p-3 md:hidden">
          {loadingFetchCampaigns ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="rounded-xl border border-slate-200 bg-slate-50 p-4 shadow-sm animate-pulse"
                >
                  <div className="h-4 w-1/3 rounded bg-slate-200 mb-2" />
                  <div className="h-3 w-1/2 rounded bg-slate-200 mb-3" />
                  <div className="flex items-center justify-between gap-3">
                    <div className="h-3 w-1/4 rounded bg-slate-200" />
                    <div className="h-3 w-1/4 rounded bg-slate-200" />
                    <div className="h-3 w-1/6 rounded bg-slate-200" />
                  </div>
                </div>
              ))}
            </div>
          ) : campaigns && campaigns.length > 0 ? (
            campaigns.map((c: WhatsappCampaign) => (
              <div
                key={c._id}
                className="rounded-xl border border-slate-200 bg-slate-50 p-4 shadow-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <h4 className="truncate text-sm font-semibold text-slate-800">
                      {c.name || "Untitled"}
                    </h4>
                    <p className="mt-1 text-xs text-slate-500">
                      {c.whatsapp.templateName || "No template"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="group relative inline-block">
                      <button
                        type="button"
                        onClick={() => handleEdit(c)}
                        aria-label="Edit"
                        title="Edit"
                        className="inline-flex items-center rounded-md bg-white border border-blue-200 px-2.5 py-1 text-xs font-medium text-blue-700 transition hover:bg-blue-50 disabled:opacity-50"
                      >
                        <Edit className="h-3.5 w-3.5" />
                      </button>
                      <span className="pointer-events-none absolute bottom-full left-1/2 mb-2 hidden -translate-x-1/2 whitespace-nowrap rounded bg-slate-800 px-2 py-1 text-xs text-white group-hover:block">
                        Edit
                      </span>
                    </div>

                    <div className="group relative inline-block">
                      <button
                        type="button"
                        onClick={() => handleViewLogs(c._id)}
                        aria-label="View logs"
                        title="View logs"
                        className="inline-flex items-center rounded-md bg-white border border-violet-200 px-2.5 py-1 text-xs font-medium text-violet-700 transition hover:bg-violet-50 disabled:opacity-50"
                      >
                        <FileText className="h-3.5 w-3.5" />
                      </button>
                      <span className="pointer-events-none absolute bottom-full left-1/2 mb-2 hidden -translate-x-1/2 whitespace-nowrap rounded bg-slate-800 px-2 py-1 text-xs text-white group-hover:block">
                        View logs
                      </span>
                    </div>
                    {(() => {
                      const normalized = (c.status || "").toLowerCase();
                      const isPaused =
                        normalized.includes("paused") ||
                        normalized.includes("pause");
                      const isRunningOrQueued =
                        normalized.includes("active") ||
                        normalized.includes("running") ||
                        normalized.includes("queued") ||
                        normalized.includes("sending") ||
                        normalized.includes("pending") ||
                        normalized.includes("scheduled");

                      if (isPaused) {
                        return (
                          <div className="group relative inline-block">
                            <button
                              type="button"
                              onClick={() => handleResume(c._id)}
                              aria-label="Resume"
                              title={loadingResume ? "Resuming..." : "Resume"}
                              disabled={loadingResume}
                              className="inline-flex items-center rounded-md bg-white border border-emerald-200 px-2.5 py-1 text-xs font-medium text-emerald-700 transition hover:bg-emerald-50 disabled:opacity-50"
                            >
                              <Play className="h-3.5 w-3.5" />
                            </button>
                            <span className="pointer-events-none absolute bottom-full left-1/2 mb-2 hidden -translate-x-1/2 whitespace-nowrap rounded bg-slate-800 px-2 py-1 text-xs text-white group-hover:block">
                              {loadingResume ? "Resuming..." : "Resume"}
                            </span>
                          </div>
                        );
                      }

                      if (isRunningOrQueued) {
                        return (
                          <div className="group relative inline-block">
                            <button
                              type="button"
                              onClick={() => handlePause(c._id)}
                              aria-label="Pause"
                              title={loadingPause ? "Pausing..." : "Pause"}
                              disabled={loadingPause}
                              className="inline-flex items-center rounded-md bg-white border border-amber-200 px-2.5 py-1 text-xs font-medium text-amber-700 transition hover:bg-amber-50 disabled:opacity-50"
                            >
                              <Pause className="h-3.5 w-3.5" />
                            </button>
                            <span className="pointer-events-none absolute bottom-full left-1/2 mb-2 hidden -translate-x-1/2 whitespace-nowrap rounded bg-slate-800 px-2 py-1 text-xs text-white group-hover:block">
                              {loadingPause ? "Pausing..." : "Pause"}
                            </span>
                          </div>
                        );
                      }

                      return null;
                    })()}
                  </div>
                </div>

                <div className="mt-3 space-y-2 text-xs text-slate-600">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-slate-500">Sender</span>
                    <span>{c.whatsapp.phoneNumber || "—"}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-slate-500">Status</span>
                    <span
                      className={`inline-flex rounded-full px-2 py-1 text-[10px] font-medium ${getStatusClasses(
                        c.status,
                      )}`}
                    >
                      {c.status || "Unknown"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-slate-500">Created</span>
                    <span>{formatDate(c.createdAt)}</span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-8 text-center text-sm text-slate-400">
              No campaigns found
            </div>
          )}
        </div>

        <div className="flex items-center justify-center gap-3 border-t border-slate-200 bg-slate-50 px-4 py-4">
          <button
            type="button"
            onClick={handlePrev}
            disabled={page === 1 || loadingFetchCampaigns}
            className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Prev
          </button>
          <button
            type="button"
            onClick={handleNext}
            disabled={loadingFetchCampaigns}
            className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      <CreateCampaignModal
        isOpen={campaignModalOpen}
        onClose={() => {
          setCampaignModalOpen(false);
          setSelectedCampaign(null);
          fetchCampaignsData();
        }}
        initialCampaign={selectedCampaign}
      />
    </div>
  );
};

export default CampaignsPage;
